export const CANVAS_WIDTH = 1920
export const CANVAS_HEIGHT = 1080

export type SlideElementType = 'text' | 'shape' | 'group' | 'stack'
export type TextRole = 'Display' | 'H1' | 'H2' | 'Body' | 'Caption'
export type ShapeKind = 'rectangle' | 'circle' | 'image'
export type TextListStyle = 'none' | 'bullet' | 'number'
export type TextSizeMode = 'auto' | 'auto-width' | 'auto-height' | 'fixed'

/** A gradient FILL mode — the gradient subset of the color picker's `ColorMode` (excludes 'solid'). */
export type GradientFillMode = 'linear' | 'radial' | 'angular'
/** One color stop of a gradient fill. A STRUCTURAL mirror of the color picker's `GradientStop`
 *  (`~/design-system/elements/ColorPicker/ColorPicker`) so the editor's stops assign directly without the
 *  model importing UI (keeps the layering one-way: UI → model). */
export interface GradientColorStop {
  id: string
  position: number
  color: string
  opacity?: number
}
/** A structured gradient fill — the editable source of truth for a gradient `background`. When set, the
 *  rendered `background` CSS is serialized from this (see `utils/gradientFill`); clearing it reverts the
 *  element to a solid fill. Kept alongside `background` so render stays a plain CSS string. */
export interface FillGradient {
  mode: GradientFillMode
  stops: GradientColorStop[]
  /** Gradient axis angle in CSS degrees (0 = ↑, 90 = →, 180 = ↓, 270 = ←). Used by linear (the axis
   *  direction) and conic (the starting angle). Linear defaults to 90 ('to right'). Set by the on-canvas
   *  rotation handle / panel angle field. */
  angle?: number
  /** Gradient centre as a percentage of the element's box, { x, y } in 0–100 (default 50/50 = centre).
   *  Used by radial & conic ("move the fill"); set by the on-canvas centre handle. */
  position?: { x: number; y: number }
  /** Radial circle radius in px (the 100% ring). Absent = CSS 'farthest-corner' default. Set by the
   *  on-canvas radius handle. */
  radius?: number
  /** LINEAR gradient endpoints as a percentage of the element's box ({ x, y } in 0–100, allowed slightly
   *  outside). When both are set they're the source of truth (free rotate / scale / move, Figma-style) and
   *  the CSS is serialized from them per current box size; absent → the box-fitted `angle` axis is used. */
  start?: { x: number; y: number }
  end?: { x: number; y: number }
}

/** One side of a per-side border: width in px, CSS color string, and line style. A `width` of 0 means the
 *  side has no border. */
export interface BorderEdge {
  width: number
  color: string
  style?: 'solid' | 'dashed' | 'dotted'
}
/** Per-side borders — the editable source of truth when the four edges differ (e.g. a card with only a
 *  coloured left accent, or a top divider). When `border` is set it fully defines all four sides (a side
 *  absent from `border` renders NO line); when absent, the uniform `strokeColor`/`strokeWidth`/`strokeStyle`
 *  paint all four sides. Kept optional so existing decks stay valid. */
export interface BorderSides {
  top?: BorderEdge
  right?: BorderEdge
  bottom?: BorderEdge
  left?: BorderEdge
}

/** One drop/inner shadow — offsets, blur, spread (px), a CSS color, and an `inset` flag. The editable
 *  source of truth for `boxShadow`: when `boxShadows` is set, the rendered `boxShadow` CSS is serialized
 *  from the list (see `utils/boxShadow`), so a stack of shadows survives editing. Modeled as a LIST from
 *  the start so stacking is additive even though the panel edits one shadow today. */
export interface ShadowSpec {
  x: number
  y: number
  blur: number
  spread: number
  color: string
  inset?: boolean
}

export type ChildSizeMode = 'fill' | 'fixed'
/** A stack frame's own sizing per axis: 'hug' auto-sizes to content, 'fixed' keeps its set size. */
export type StackFrameSizeMode = 'hug' | 'fixed'
export type StackDirection = 'row' | 'column' | 'grid'
export type StackAlign = 'start' | 'center' | 'end' | 'stretch'
export type StackJustify =
  | 'start'
  | 'center'
  | 'end'
  | 'space-between'
  | 'space-around'
  | 'space-evenly'
export type StackWrap = 'nowrap' | 'wrap'

export interface StackLayout {
  direction: StackDirection
  gap: number
  paddingTop: number
  paddingRight: number
  paddingBottom: number
  paddingLeft: number
  align: StackAlign
  justify: StackJustify
  wrap: StackWrap
  /** Frame sizing per axis (default 'hug'). 'fixed' = the frame keeps its set width/height and
   *  children distribute / fill within it instead of the frame hugging them. */
  widthMode?: StackFrameSizeMode
  heightMode?: StackFrameSizeMode
  /** Grid mode only (direction === 'grid'). Column count for row-major placement; default 2. */
  columns?: number
  /** Grid mode only. Per-axis gaps; fall back to `gap` when unset. */
  rowGap?: number
  columnGap?: number
}

export interface SlideCode {
  typescript: string
  javascript: string
  html: string
  css: string
}

export interface SlideElement {
  id: string
  type: SlideElementType
  x: number
  y: number
  width: number
  height: number
  rotation?: number
  zIndex: number
  text?: string
  /** Inline rich text: the element's content as a small sanitized inline-HTML string (bold / italic /
   *  underline / colored or sized spans). Set when a generated text node had inline formatting; the
   *  renderer injects it (sanitized) instead of plain `text`. `text` stays the plaintext fallback
   *  (auto-size measuring, search, non-rich consumers). Undefined → plain `text` is authoritative. */
  textHtml?: string
  textRole?: TextRole
  fontSize?: number
  lineHeight?: number
  fontFamily?: string
  fontWeight?: number
  fontStyle?: 'normal' | 'italic'
  letterSpacing?: number
  textAlign?: 'left' | 'center' | 'right'
  listStyle?: TextListStyle
  textSizeMode?: TextSizeMode
  color?: string
  /** Gradient painted through the glyphs (CSS background-image clipped to text). Preserves a generated
   *  gradient heading. When set the renderer applies background-clip:text + transparent fill. */
  textGradient?: string
  /** CSS text-shadow string, preserved verbatim from generation. */
  textShadow?: string
  opacity?: number
  background?: string
  /** Structured gradient fill (editable source of truth). When set, `background` is serialized from this
   *  via `utils/gradientFill`; clearing it reverts to the solid `background` color. */
  fillGradient?: FillGradient
  /** CSS box-shadow string (shapes/cards), preserved verbatim from generation. When `boxShadows` is set it
   *  is the serialized form of that list (see {@link ShadowSpec}); editing shadows updates both. */
  boxShadow?: string
  /** Structured box-shadow stack (editable source of truth). When present, `boxShadow` is serialized from
   *  this; clearing it removes the shadow. */
  boxShadows?: ShadowSpec[]
  fillImage?: string
  shapeFillMode?: 'color' | 'image'
  /** How a fillImage maps onto the box: 'cover' (default — photos, crops to fill), 'contain'
   *  (logos/marks — never cropped, letterboxed over the element's background instead), or 'fill'
   *  (stretched to the box, ignoring aspect ratio). */
  imageFit?: 'cover' | 'contain' | 'fill'
  /** CSS object-position the image was generated with (e.g. 'left' for a logo pinned to the edge).
   *  Maps to background-position on the filled box; undefined → centered (the default). */
  imagePosition?: string
  /** A TILING / REPEATING background pattern (grid, lines, dots) keeps its CSS `background` string and
   *  these so it tiles across the box instead of rendering once. Only set for patterns; a single image
   *  uses fillImage/imageFit instead. `backgroundRepeat` e.g. 'repeat' / 'repeat-x'; `backgroundSize` the
   *  tile size e.g. '40px 40px' (omitted when the tile is its natural size). */
  backgroundRepeat?: string
  backgroundSize?: string
  /** CSS filter / backdrop-filter, preserved verbatim from generation so blur and frosted-glass effects
   *  survive the bake. Undefined when the element has none. */
  filter?: string
  backdropFilter?: string
  /** Layer (element) blur radius in px — a structured `filter: blur()`. The editable source of truth: when
   *  set it composes into the rendered `filter` alongside any preserved non-blur `filter`. Figma allows one
   *  layer blur per node, so this is a scalar (see `utils/blur`). */
  layerBlur?: number
  /** Background (backdrop) blur radius in px — a structured `backdrop-filter: blur()` for frosted-glass.
   *  Composes into the rendered `backdropFilter`; one per node. */
  backdropBlur?: number
  shapeKind?: ShapeKind
  strokeColor?: string
  strokeWidth?: number
  strokeStyle?: 'solid' | 'dashed' | 'dotted'
  /** Where a uniform stroke paints relative to the element box: 'inside' (default — within the bounds, the
   *  historical behaviour), 'center' (straddling the edge) or 'outside' (beyond the bounds). Center/outside
   *  render via a stroke overlay (see `utils/elementRenderStyle`); per-side `border` is always inside. */
  strokeAlign?: 'inside' | 'center' | 'outside'
  /** Per-side borders. When set, overrides the uniform `stroke*` and renders each edge independently (see
   *  {@link BorderSides}); baker-populated when a generated card's four edges differ, and editable via the
   *  shape panel's per-side border control. */
  border?: BorderSides
  cornerRadius?: number
  /** Per-corner radii [topLeft, topRight, bottomRight, bottomLeft] for non-uniform corners (e.g. a card
   *  rounded only on top). Set only when the corners differ; otherwise the scalar `cornerRadius` is used. */
  cornerRadii?: [number, number, number, number]
  // Grouping / nesting — all optional so existing decks stay valid.
  // parentId null/undefined = top-level on the slide. childIds (groups & stacks)
  // is the authoritative child order. stack present only when type === 'stack'.
  parentId?: string | null
  childIds?: string[]
  stack?: StackLayout
  widthMode?: ChildSizeMode
  heightMode?: ChildSizeMode
  flexGrow?: number
  collapsed?: boolean
  name?: string
}

export interface Slide {
  id: string
  title: string
  elements: SlideElement[]
  background?: string
  /** Structured gradient fill for the slide background (editable source of truth, serialized to
   *  `background` for render — mirrors {@link SlideElement.fillGradient}). */
  fillGradient?: FillGradient
  fillImage?: string
  slideFillMode?: 'color' | 'image'
  /** Free world position of the slide's top-left (same space as canvasElements / documentCards). When
   *  set, it's the source of truth for the slide's canvas position; when absent (legacy decks, fresh AI
   *  output) the position falls back to the grid math from `gridCol`/`gridRow`. */
  x?: number
  y?: number
  /** Legacy grid coordinates. Still emitted by the AI generator + seeded on create as a fallback for
   *  `x`/`y`; deck ORDER is no longer derived from these (it's a spatial scan of x/y — see
   *  `sortSlidesSpatially`). */
  gridCol?: number
  gridRow?: number
  /** When true, the slide is skipped in Present mode (default/undefined = included). */
  excludedFromPresentation?: boolean
  /** Cross-type CANVAS stacking order (shared with notes/docs/images/web cards) — distinct from the
   *  per-element zIndex inside `elements`. Dragging the slide bumps it to the top of the canvas. Absent on
   *  legacy decks → treated as 0. See utils/canvasItemZIndex. */
  canvasZIndex?: number
  /** Which slide group (presentation frame) this slide belongs to, or null/undefined when ungrouped.
   *  Mirrors SlideElement.parentId — membership is a flat pointer; the frame geometry is derived. */
  groupId?: string | null
  code: SlideCode
  createdAt: number
  updatedAt: number
}

/** A "presentation" frame grouping a set of top-level slides on the canvas (a Figma-Section-style box).
 *  Identity only — the frame's rect is DERIVED each render from its member slides' world positions, never
 *  stored, so it can never desync under drag/duplicate/delete/undo. A group always has ≥2 members
 *  (auto-dissolves below that). */
export interface SlideGroup {
  id: string
  /** Editable display name shown in the frame header; defaults to "Presentation N". */
  title: string
  createdAt: number
  updatedAt: number
}

/** A free-form user document card living on the canvas world (not derived from AI sections).
 *  Position/size are in WORLD coordinates, the same space as slides and canvasElements. */
export interface DocumentCard {
  id: string
  x: number
  y: number
  width: number
  height: number
  title: string
  content: string
  /** Cross-type canvas stacking order. Dragging an item bumps it to the top across ALL canvas item
   *  kinds (notes/docs/images/web cards/slides share one z space). Absent on legacy decks → treated as 0
   *  (the group paint order is the tiebreaker). See utils/canvasItemZIndex. */
  zIndex?: number
  /** Agent-owned card (Goal / one outline canvas). Rendered like any document card — draggable,
   *  selectable, position persisted — but its `content` is synced from the planning sections (seeded
   *  once, then the user owns its position/deletion). Opening it routes to the agent-doc reader: Goal
   *  and `canvas` cards are EDITABLE and save back to their section (a `canvas` card writes its one
   *  canvas's title/body into the structured OutlineData). Absent/false on a normal user doc. */
  locked?: boolean
  /** Which agent artifact this locked card mirrors: the Goal markdown, the (legacy) whole Outline, or
   *  ONE canvas of the outline (`canvas` → see `canvasId`). */
  source?: 'goal' | 'outline' | 'canvas'
  /** For a `source: 'canvas'` card: the id of the outline canvas this doc reflects. Edits write back
   *  to that canvas's title/content in the structured OutlineData (the outline stays source of truth). */
  canvasId?: string
  /** When set, this card is a PLACEMENT of a Library {@link ConversationDocument}: its content/title
   *  hydrate from that document, edits persist back to it, and removing the card just unplaces it (the
   *  document survives in the Library). Deleting the document from the Library removes the card too.
   *  Absent on a purely local/blank canvas doc. */
  documentId?: string
  /** When set, this doc card belongs to a {@link DocumentGroup} (a "deck" frame bundling docs that
   *  generate together). null/undefined = ungrouped. Mirrors `Slide.groupId`. */
  groupId?: string | null
  createdAt: number
  updatedAt: number
}

/** A document group = a "deck" frame bundling a set of doc cards on the canvas. The grouped docs can be
 *  generated into a deck in one shot (one slide per doc). Identity lives here + on `DocumentCard.groupId`;
 *  the frame's geometry is derived per render from the members' positions (never stored). Mirrors
 *  {@link SlideGroup}. */
export interface DocumentGroup {
  id: string
  /** Editable display name shown in the frame header; defaults to "Deck N". */
  title: string
  createdAt: number
  updatedAt: number
}

/** A UNIVERSAL canvas group: one frame bundling ANY mix of top-level canvas items — notes, docs, web
 *  cards, images, moodboards, slides. Identity only; the frame's rect is DERIVED each render from its
 *  members' world rects (never stored, so it can't desync under drag/duplicate/delete/undo). Membership is
 *  a flat `groupId` pointer on each item (see each kind). A group always has ≥2 members (auto-dissolves
 *  below that). Supersedes {@link SlideGroup} + {@link DocumentGroup}, which migrate into this on load.
 *  Behaviour is composition-adaptive (see utils/canvasGroup): an all-slide group keeps the presentation
 *  niceties (row arrange + Share/Present/Export); any other mix is a plain free group. */
export interface CanvasGroup {
  id: string
  /** Editable display name shown in the frame header; defaults to "Group N". */
  title: string
  /** Collapsed = hide every NON-slide member (the wired source cards) and show only the slide, with an
   *  "N hidden" badge on the frame to expand. Render-only (membership is untouched, so the group never
   *  dissolves while collapsed). Persisted; only meaningful for groups that contain a slide. */
  collapsed?: boolean
  /** Manually-set frame geometry (world px). When all four are present the box is a FIXED, resizable
   *  frame at this rect (still clamped to always contain its members + padding); when absent the box
   *  auto-fits its members (legacy behavior). Seeded at creation and on first manual resize. */
  x?: number
  y?: number
  width?: number
  height?: number
  createdAt: number
  updatedAt: number
}

/** Soft background tint for a note sticky. A key (not a raw hex) so it re-resolves through the
 *  `--color-basic-*-subtle` design tokens and stays theme-aware. `default` (or absent) = white. */
export type NoteColor = 'default' | 'gray' | 'yellow' | 'orange' | 'red' | 'green' | 'blue'

/** A note card on the canvas world — a square sticky whose text is edited inline (no fullscreen
 *  editor, unlike DocumentCard). Same world-coordinate space as slides / docs / canvasElements.
 *  Content is a markdown string (same Tiptap round-trip as docs). */
export interface Note {
  id: string
  x: number
  y: number
  width: number
  height: number
  content: string
  /** Soft background tint; absent → white. Set via the hover swatch bar. */
  color?: NoteColor
  /** Cross-type canvas stacking order (shared across all canvas item kinds). See utils/canvasItemZIndex. */
  zIndex?: number
  /** When set, this note belongs to a {@link CanvasGroup}. null/undefined = ungrouped. */
  groupId?: string | null
  createdAt: number
  updatedAt: number
}

/** An image living directly on the canvas world as its OWN element (NOT a slide element / canvasElement
 *  — it never attaches to a slide). Opens fullscreen on click like a doc card. Same world-coordinate
 *  space as slides / docs / notes. */
export interface CanvasImage {
  id: string
  x: number
  y: number
  width: number
  height: number
  /** Image source — an inline dataURL (re-encoded/bounded for localStorage) or a remote image URL. */
  src: string
  /** Intrinsic pixel size of the source (drives aspect ratio for the fullscreen fit). */
  naturalWidth: number
  naturalHeight: number
  /** True when `src` is a vector SVG. Kept as the original markup (never rasterized) and rendered
   *  `object-contain` over a transparent card so logos/icons stay crisp at any zoom and don't get cropped. */
  isSvg?: boolean
  /** Render over a TRANSPARENT card with `object-contain` (like an SVG) instead of the default white,
   *  cover-cropped card. Set for imported design frames (e.g. a Figma frame) whose PNG may carry its own
   *  transparency — so a transparent frame isn't boxed in white. The src is still a normal raster URL. */
  transparent?: boolean
  /** The web page this image was extracted from ("Extract images" on a web card). Lets a moodboard
   *  built from extracted images still reach the page's CSS brand tokens (exact fonts/hexes). */
  sourceUrl?: string
  /** Cross-type canvas stacking order (shared across all canvas item kinds). See utils/canvasItemZIndex. */
  zIndex?: number
  /** When set, this image belongs to a {@link CanvasGroup}. null/undefined = ungrouped. */
  groupId?: string | null
  createdAt: number
  updatedAt: number
}

/** A brand's design tokens pulled from a website's CSS (backend BrandStyleExtractionService): colors with
 *  roles + fonts + logo. Generation adopts these to make a deck look like the brand. */
export interface BrandStyle {
  url?: string
  /** Top distinct brand colors (hex), most prominent first. */
  palette: string[]
  /** Semantic color roles — subset of: background, surface, text, textMuted, accent, border. */
  roles: Record<string, string>
  headingFont?: string
  bodyFont?: string
  spacing?: string[]
  logoUrl?: string
  /** Typographic facts from the CSS — subset of: headingSize, headingWeight, headingTransform,
   *  headingLetterSpacing, bodySize, radius. What makes generated type match the brand. */
  typography?: Record<string, string>
  /** The site's own @font-face files, so the client can load the REAL brand typeface. */
  fontFaces?: { family: string; url: string; weight?: string; style?: string }[]
  /**
   * The brand's TYPE SYSTEM: five named roles, each with a face, size, weight, tracking and a usage rule.
   * See `style/typeScale.ts`.
   *
   * Lives here rather than in `typography` above because that field is a flat `Record<string,string>` of
   * single CSS facts the backend extractor writes, and this is a structured object the CLIENT owns and
   * edits. Riding `tokens` means it round-trips through the existing wire with no backend change at all —
   * the API treats that column as opaque JSON, by design.
   */
  typeScale?: import('~/design-system/components/DeckGen/style/typeScale').TypeScale
}

/** A website card on the canvas world — paste a URL and it renders the site as a scrollable full-page
 *  screenshot (live-iframing most sites is blocked by X-Frame-Options/CSP, so we screenshot instead).
 *  Select it to extract the page's images into the Library. Same world-coordinate space as slides /
 *  docs / notes / images. */
export interface WebCard {
  id: string
  x: number
  y: number
  width: number
  height: number
  /** Card variant: a live website preview (`web`, the default), a single colour-swatch tile (`swatch`),
   *  a fonts tile (`fonts`), or a logo tile (`logo`). Tiles reuse the web-card plumbing (drag/select/
   *  persist) with a small headerless body — the brand style extracts into a row of swatch tiles + one
   *  fonts tile (+ a logo tile when the page exposed one). */
  kind?: 'web' | 'swatch' | 'fonts' | 'logo'
  /** For kind 'swatch': the colour to fill + its role label (e.g. "Accent"); the hex shows on hover. */
  swatch?: { color: string; label?: string }
  /** For kind 'fonts': the brand heading/body font names, rendered in their real typefaces. */
  fonts?: { heading?: string; body?: string }
  /** For kind 'logo': the brand logo image, framed on a neutral tile. */
  logo?: { src: string }
  /** The website URL the user pasted (canonicalised by the backend). */
  url: string
  /** Page title from the scrape (shown in the card header); empty until ready. */
  title?: string
  /** Our stored full-page screenshot URL (the scrollable preview); absent while loading. */
  screenshotUrl?: string
  /** True when the site has no X-Frame-Options / CSP frame-ancestors block, so it can be shown LIVE in an
   *  iframe — the canvas renders the live site (and the fullscreen viewer is fully interactive) instead of
   *  the screenshot. Most locked-down SaaS is false → screenshot fallback. */
  frameable?: boolean
  /** empty = blank card awaiting a URL (created from the toolbar), loading = fetching the preview,
   *  ready = screenshot shown, error = couldn't render the site. */
  status: 'empty' | 'loading' | 'ready' | 'error'
  /** Candidate image URLs scraped from the page, for the "Extract images" action (default []). */
  sourceImages?: string[]
  /** A social post (X / Substack / …) the backend extracted instead of a screenshot — sites like X block
   *  framing AND screenshotting, so for those we render the post itself (author + text + images) right in
   *  the card. Set ⇒ the card shows a SocialBody, not a screenshot. The Extract bar still works off it. */
  post?: {
    platform: string
    author?: string
    /** The author's avatar image URL (shown in the post header); falls back to an initial when absent. */
    authorAvatar?: string
    text: string
    images: string[]
    /** Playable videos in the post (X video tweets / video cards) — a streamable mp4 + poster + aspect dims. */
    videos?: { url: string; poster?: string; width?: number; height?: number }[]
  }
  /** Brand design tokens extracted from the page CSS (lazily, after preview). Drives styled generation. */
  brandStyle?: BrandStyle
  /** Cross-type canvas stacking order (shared across all canvas item kinds). See utils/canvasItemZIndex. */
  zIndex?: number
  /** When set, this web card belongs to a {@link CanvasGroup}. null/undefined = ungrouped. */
  groupId?: string | null
  createdAt: number
  updatedAt: number
}

/** One reference collected into a moodboard: a canvas image or a website (via its screenshot). */
export interface MoodboardMember {
  kind: 'image' | 'web'
  /** Image source for thumbnails + style synthesis — the canvas image src or the web card screenshot. */
  src: string
  /** The source page URL — the web card's url, or the page an extracted image came from. Synthesis
   *  CSS-extracts brand tokens (exact fonts/hexes) from every distinct member url. */
  url?: string
}

/** The synthesized style of a moodboard — what generation consumes ("the reference"). */
export interface MoodboardProfile {
  /** What this collection's aesthetic IS — a short paragraph explaining the common style. */
  tasteProfile: string
  /** Style keywords shared across the members (chips on the card). */
  keywords: string[]
  /** What to avoid — styles present in none of the members that a generator might wrongly reach for. */
  avoids: string[]
  /** The generation-ready prompt block: concrete art direction a slide generator should follow. */
  guideline: string
  /** Unified palette across the members (hex, most prominent first). */
  palette: string[]
  /** Semantic color roles (same contract as {@link BrandStyle.roles}: background, surface, text,
   *  textMuted, accent, border) — these drive the proven `## Brand style` generation block. */
  roles?: Record<string, string>
  headingFont?: string
  bodyFont?: string
  /** Tone of voice for COPY synthesized from the references (e.g. "Confident, plain, short — no hype").
   *  Drives the WORDING of generated text, not just the visual look. Optional (older profiles omit it). */
  tone?: string
  /** Type hierarchy in words: heading scale/weight/casing + body size/weight (e.g. "Display headlines
   *  120px+ bold uppercase; 18px regular body"). Renders as a row in the design-system guide. Optional. */
  typography?: string
  /** Spacing / layout rhythm (e.g. "Generous margins, 80px gutters, lots of negative space"). Optional. */
  spacing?: string
  /** Shadow / elevation rule (e.g. "Flat, no shadows" or "Soft 0 2px 8px rgba(0,0,0,.08)"). Optional. */
  shadows?: string
  /** Usage RULES — the "how to use this system" the consistency reduce extracts from reading every slide:
   *  how each colour/element is actually used across the deck, stated as concrete do/don't rules (e.g.
   *  "Accent red: single-word emphasis + bar fills, never a page background"). This is what lets generation
   *  reuse the system creatively instead of copying one slide. Drives the prompt block; optional. */
  patterns?: string[]
  /**
   * STANDING RULES the user gave and expects to hold on the NEXT document, and the one after that.
   *
   * Their own field, deliberately, and never folded into {@link patterns}. `patterns` is read by
   * `matchLayoutPatterns`, which matches on words like stack, grid, tiles and one number, so
   * "never stack two adjectives in a headline" stored there would come back out as a request for the
   * STACK layout family. A rule is not a layout preference and must not be parsed as one.
   */
  rules?: BrandRule[]
  /** Written lines that SHOW the voice, where {@link tone} only describes it. A model matches a sample
   *  far more reliably than it matches an adjective. */
  toneExamples?: ToneExample[]
}

/** How far a rule reaches. ONE list holds all three, scoped on the entry, because a rule written onto some
 *  other record is a rule that has to survive that record's own reader. */
export type BrandRuleScope = 'kit' | 'composition' | 'template'

/** One standing rule. `origin` separates what the agent inferred from what the user said, so a later pass
 *  can weigh them differently without guessing; `reason` is the WHY, shown in the kit, never sent to a model
 *  (a rule that has to argue for itself is a rule the model will debate). */
export interface BrandRule {
  id: string
  text: string
  scope: BrandRuleScope
  /** Epoch ms it was written. */
  at: number
  origin: 'agent' | 'user'
  /** The composition role this was learned on. Only ever set when `scope` is `composition`. */
  role?: string
  reason?: string
}

/** Which piece of copy an example is an example OF. Closed, because it is what a generation matches
 *  against: an example filed under a slot nobody writes is an example nothing reads. */
export type ToneExampleSlot = 'headline' | 'body' | 'caption' | 'cta'

/** `do` = write like this. `dont` = a real line the brand rejected, which teaches more than a rule about
 *  it. `sample` = neither, just a specimen of the voice. */
export interface ToneExample {
  id: string
  kind: 'do' | 'dont' | 'sample'
  slot: ToneExampleSlot
  text: string
  note?: string
}

/** A moodboard card on the canvas world: a curated group of images / websites synthesized into one
 *  style profile ("Make a mood board" on a selection). The profile is the style REFERENCE handed to
 *  deck/slide generation; the member images double as visual style refs. Same world-coordinate space
 *  as the other canvas items. */
export interface Moodboard {
  id: string
  x: number
  y: number
  width: number
  height: number
  /** Editable display name; the synthesis proposes one (e.g. "Ethereal Pastel Landscapes"). */
  title: string
  members: MoodboardMember[]
  /** synthesizing = vision call in flight, ready = profile present, error = synthesis failed (retry). */
  status: 'synthesizing' | 'ready' | 'error'
  profile?: MoodboardProfile
  /** Cross-type canvas stacking order (shared across all canvas item kinds). See utils/canvasItemZIndex. */
  zIndex?: number
  /** When set, this moodboard belongs to a {@link CanvasGroup}. null/undefined = ungrouped. */
  groupId?: string | null
  createdAt: number
  updatedAt: number
}

/** A saved, NAMED design system in a deck's library (Artifact A). Built once via "Synthesize" from any
 *  mix of sources — a PDF (pages rendered to images), loose images, and websites (CSS brand) — into one
 *  reusable style spec, then recalled by name as the active "Style" from the generation bar and the chat
 *  agent panel. The `profile` is the SAME synthesis shape moodboards produce, so every generation path
 *  (the `## Brand style` block + the prompt block) is reused unchanged. The active system is
 *  {@link Presentation.activeDesignSystemId}. */
export interface DesignSystem {
  id: string
  /** Editable display name; synthesis proposes one (e.g. "SPACE10 Editorial"). */
  name: string
  /** Which intake sources fed this system — for the guide's provenance and re-synthesis. */
  sources?: { pdf?: string[]; images?: string[]; websites?: string[] }
  /** The synthesized style spec (taste, palette, fonts, tone, typography, spacing, shadows, guideline). */
  profile: MoodboardProfile
  /** Display-only example layout thumbnails pulled from a source PDF. Functional layout RETRIEVAL
   *  (Artifact B) comes later; for now these just illustrate the system in the guide. Default []. */
  examples?: string[]
  createdAt: number
  updatedAt: number
}

/** The canvas-item kinds a connection can START from (the generation inputs). A `slide` source is a
 *  finished slide wired into ANOTHER slide as a strict 1:1 STYLE template — the model captures the source
 *  slide and reproduces its exact look (palette, type, layout language) for global deck consistency. */
export type ConnectionSourceKind = 'note' | 'document' | 'moodboard' | 'webcard' | 'image' | 'slide'

/** A node-style wire on the canvas: an INPUT (text note / document / moodboard) connected INTO a slide.
 *  Connections drive granular generation — "generate THIS slide from THESE inputs". Geometry is derived
 *  from the endpoints each render (never stored), so wires follow their items; a wire whose endpoint was
 *  deleted simply stops rendering (lazy cleanup). */
export interface CanvasConnection {
  id: string
  from: { kind: ConnectionSourceKind; id: string }
  to: { kind: 'slide'; id: string }
  /** How a wired IMAGE feeds generation (chosen when the wire is dropped on a slide):
   *  - `style`   = use the image as a STYLE reference (synthesized like a 1-image moodboard);
   *  - `content` = place the image IN the slide (analyzed so the model knows what it is).
   *  Absent/irrelevant for note/document/moodboard/webcard sources (they have one fixed meaning).
   *  Defaults to `content` for a legacy image wire with no mode. */
  mode?: 'style' | 'content'
  /** For a `style` image wire: the cached style synthesis (palette/fonts/guideline), run once on drop and
   *  reused at generation so we don't re-call the vision model on every generate. */
  styleProfile?: MoodboardProfile
  /** Synthesis lifecycle for a `style` image wire (drives the port's spinner/icon). */
  styleStatus?: 'synthesizing' | 'ready' | 'error'
  /** How strongly a `style` image (or `moodboard`) wire is applied at generation, 0–100 (default 100).
   *  Maps to prompt strictness language (strict → close → light inspiration → off), NOT a diffusion knob —
   *  this is a text-to-slide generator. Set via the on-image reveal control. */
  styleStrength?: number
  createdAt: number
}

export interface Presentation {
  id: string
  title: string
  slides: Slide[]
  /** Free elements that belong to no slide, stored in WORLD coordinates (default []). */
  canvasElements: SlideElement[]
  /** User-created blank document cards on the canvas world (default []). */
  documentCards?: DocumentCard[]
  /** User-created note cards on the canvas world (default []). */
  notes?: Note[]
  /** User-added image elements on the canvas world (default []). */
  canvasImages?: CanvasImage[]
  /** User-pasted website cards on the canvas world (default []). */
  webCards?: WebCard[]
  /** Moodboards (style references synthesized from grouped images/websites; default []). */
  moodboards?: Moodboard[]
  /** v1 single-lock deck-level style (one profile applied to every generation). SUPERSEDED by the
   *  {@link designSystems} library + {@link activeDesignSystemId} below; kept for back-compat. STYLE only —
   *  layout stays free per slide. Default undefined. */
  theme?: MoodboardProfile
  /** The deck's LIBRARY of saved design systems (Artifact A). Build several from PDF/images/websites, pick
   *  one as the active "Style" via {@link activeDesignSystemId}. The library form of {@link theme}. Default []. */
  designSystems?: DesignSystem[]
  /** Which {@link designSystems} entry is the ACTIVE style applied to generation by default. When set, its
   *  profile is the deck-level style. Default undefined. */
  activeDesignSystemId?: string
  /** Node-style wires connecting input items (notes/docs/moodboards) into slides (default []). */
  connections?: CanvasConnection[]
  /** Universal canvas groups bundling ANY mix of canvas items (notes/docs/web/images/moodboards/slides;
   *  default []). The source of truth post-migration; {@link slideGroups}/{@link documentGroups} below are
   *  legacy and folded into this on load (see store/presentationFactory `migrateCanvasGroups`). */
  canvasGroups?: CanvasGroup[]
  /** @deprecated Legacy slide-only groups; migrated into {@link canvasGroups} on load. */
  slideGroups?: SlideGroup[]
  /** @deprecated Legacy doc-only groups; migrated into {@link canvasGroups} on load. */
  documentGroups?: DocumentGroup[]
  /** Agent sections ('goal'/'outline') whose canvas card has already been seeded once for this deck.
   *  Once a source is here it's never auto-re-created — so moving or deleting the card sticks across
   *  refreshes (the card is the user's after first placement). Default []. */
  seededAgentSources?: string[]
  activeSlideId: string
  selectedSlideIds: string[]
  selectedElementIds: string[]
  /** Document cards currently selected on the canvas (multi-select for duplicate/delete; default []). */
  selectedDocumentCardIds?: string[]
  /** Note cards currently selected on the canvas (default []). */
  selectedNoteIds?: string[]
  /** Canvas images currently selected on the canvas (default []). */
  selectedCanvasImageIds?: string[]
  /** Website cards currently selected on the canvas (default []). */
  selectedWebCardIds?: string[]
  /** Moodboards currently selected on the canvas (default []). */
  selectedMoodboardIds?: string[]
  schemaVersion: number
  createdAt: number
  updatedAt: number
}
